/**
 * Auto-routing engine: a tide-and-wind-aware route from start to destination.
 *
 * A* over a coarse navigable grid, time-dependent: the cost of crossing an edge
 * depends on WHEN you arrive there, because the tidal stream (and a sailing
 * boat's speed against the wind) change through the passage. The clock is carried
 * along each path so the tide is read at the right moment.
 *
 * The cost itself is supplied by the caller (`edgeCost`), so this stays a pure,
 * testable search: the real wiring builds edgeCost from the chart depths, the
 * diamonds and the wind/polar; tests inject simple fields. A blocked edge (too
 * shallow, land, a hazard, or a tide the boat can't beat) is signalled by
 * returning null.
 *
 * Honest about its limits: a coarse grid, sparse tide and coarse wind make this a
 * SUGGESTED route to check at high zoom, not an optimum. The caller gates it so.
 */
import { rhumbBearingDeg, rhumbDistanceNm } from '@/nav';

export interface GridSpec {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
  cols: number;
  rows: number;
}

export interface EdgeCost {
  /** Real elapsed time to traverse, hours (drives the tide clock). */
  timeH: number;
  /** Extra soft cost for ranking (hazard nearness, thin depth margin…), >= 0. */
  penalty: number;
}

export interface AutoRouteParams {
  grid: GridSpec;
  start: [number, number];
  destination: [number, number];
  departMs: number;
  /** Cost from->to departing at ms; null = blocked. */
  edgeCost: (from: [number, number], to: [number, number], ms: number) => EdgeCost | null;
  /** Optimistic speed (kn) for the A* heuristic — keep it >= any achievable SOG. */
  heuristicSpeedKn: number;
  /** Safety cap on nodes expanded. Default cols*rows*8. */
  maxExpand?: number;
  /**
   * Is this grid node navigable at all (land / depth check). Optional. When given,
   * the search ANCHORS the start and destination on the nearest node that passes
   * this test, rather than the raw nearest node. That matters when the chosen
   * start or destination sits over charted-shallow water or right on the coast: the
   * raw nearest node is then unnavigable, no edge can enter it, and A* would return
   * null — which upstream turns into a straight line drawn through every hazard. By
   * anchoring on the nearest genuinely navigable node instead, A* still threads a
   * clear channel; the exact (shallow) endpoint is re-attached as a short approach
   * leg the caller warns about. Only if there's NO navigable node near an endpoint,
   * or no channel between the two anchors, do we return null and let the caller fall
   * back to a direct line.
   */
  passable?: (pos: [number, number]) => boolean;
  /**
   * When anchoring a non-navigable endpoint on nearby navigable water (see
   * `passable`), reject a candidate anchor whose straight approach to the EXACT
   * endpoint isn't clear — chiefly, one that would cross land. Optional; without it
   * any navigable anchor is accepted. This keeps the short approach leg to a
   * charted-shallow destination from cutting across a headland: the approach may
   * pass over the shallow around the endpoint (that's the warned bit), but never
   * over land.
   */
  endpointApproachClear?: (anchor: [number, number], endpoint: [number, number]) => boolean;
}

export interface AutoRouteResult {
  waypoints: [number, number][];
  totalTimeH: number;
  expanded: number;
}

const MS_PER_H = 3_600_000;

/** Tiny binary min-heap keyed by priority. */
class MinHeap {
  private p: number[] = [];
  private v: number[] = [];
  get size(): number {
    return this.v.length;
  }
  push(priority: number, value: number): void {
    this.p.push(priority);
    this.v.push(value);
    let i = this.v.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.p[parent] <= this.p[i]) break;
      this.swap(i, parent);
      i = parent;
    }
  }
  pop(): number | undefined {
    if (!this.v.length) return undefined;
    const top = this.v[0];
    const lastP = this.p.pop()!;
    const lastV = this.v.pop()!;
    if (this.v.length) {
      this.p[0] = lastP;
      this.v[0] = lastV;
      let i = 0;
      const n = this.v.length;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let s = i;
        if (l < n && this.p[l] < this.p[s]) s = l;
        if (r < n && this.p[r] < this.p[s]) s = r;
        if (s === i) break;
        this.swap(i, s);
        i = s;
      }
    }
    return top;
  }
  private swap(a: number, b: number): void {
    [this.p[a], this.p[b]] = [this.p[b], this.p[a]];
    [this.v[a], this.v[b]] = [this.v[b], this.v[a]];
  }
}

export function autoRoute(params: AutoRouteParams): AutoRouteResult | null {
  const { grid, start, destination, departMs, edgeCost, heuristicSpeedKn } = params;
  const { cols, rows } = grid;
  if (cols < 2 || rows < 2) return null;
  const n = cols * rows;
  const maxExpand = params.maxExpand ?? n * 8;

  const { passable, endpointApproachClear } = params;
  const lonOf = (c: number) => grid.minLon + ((grid.maxLon - grid.minLon) * c) / (cols - 1);
  const latOf = (r: number) => grid.minLat + ((grid.maxLat - grid.minLat) * r) / (rows - 1);
  const posOf = (idx: number): [number, number] => [lonOf(idx % cols), latOf(Math.floor(idx / cols))];
  const nearest = (pos: [number, number]): number => {
    const c = Math.round(((pos[0] - grid.minLon) / (grid.maxLon - grid.minLon)) * (cols - 1));
    const r = Math.round(((pos[1] - grid.minLat) / (grid.maxLat - grid.minLat)) * (rows - 1));
    const cc = Math.max(0, Math.min(cols - 1, c));
    const rr = Math.max(0, Math.min(rows - 1, r));
    const base = rr * cols + cc;
    // No navigability test, or the raw nearest node is already navigable: done.
    if (!passable || passable(posOf(base))) return base;
    // The chosen point is over unnavigable water (shallow/land). Spiral out in
    // square rings and anchor on the CLOSEST navigable node, so the search starts
    // and ends in water it can actually move through instead of failing outright.
    const maxRad = Math.max(cols, rows);
    for (let rad = 1; rad < maxRad; rad++) {
      let best = -1;
      let bestD = Infinity;
      for (let dr = -rad; dr <= rad; dr++) {
        for (let dc = -rad; dc <= rad; dc++) {
          if (Math.max(Math.abs(dr), Math.abs(dc)) !== rad) continue; // ring edge only
          const r2 = rr + dr;
          const c2 = cc + dc;
          if (r2 < 0 || r2 >= rows || c2 < 0 || c2 >= cols) continue;
          const idx = r2 * cols + c2;
          const nodePos = posOf(idx);
          if (!passable(nodePos)) continue;
          if (endpointApproachClear && !endpointApproachClear(nodePos, pos)) continue;
          const d = rhumbDistanceNm(pos, nodePos);
          if (d < bestD) {
            bestD = d;
            best = idx;
          }
        }
      }
      if (best !== -1) return best; // nearest ring with any navigable node wins
    }
    return base; // nothing navigable anywhere: A* will fail and the caller falls back
  };

  const startIdx = nearest(start);
  const destIdx = nearest(destination);
  const destPos = posOf(destIdx);
  const heur = (idx: number) => rhumbDistanceNm(posOf(idx), destPos) / Math.max(0.1, heuristicSpeedKn);

  const cost = new Float64Array(n).fill(Infinity);
  const timeH = new Float64Array(n).fill(Infinity);
  const came = new Int32Array(n).fill(-1);
  const closed = new Uint8Array(n);

  cost[startIdx] = 0;
  timeH[startIdx] = 0;
  const open = new MinHeap();
  open.push(heur(startIdx), startIdx);
  let expanded = 0;

  while (open.size) {
    const cur = open.pop()!;
    if (closed[cur]) continue;
    closed[cur] = 1;
    if (cur === destIdx) break;
    if (++expanded > maxExpand) break;

    const cr = Math.floor(cur / cols);
    const cc = cur % cols;
    const curPos = posOf(cur);
    const curMs = departMs + timeH[cur] * MS_PER_H;

    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nr = cr + dr;
        const ncc = cc + dc;
        if (nr < 0 || nr >= rows || ncc < 0 || ncc >= cols) continue;
        const nb = nr * cols + ncc;
        if (closed[nb]) continue;
        const nbPos = posOf(nb);
        const ec = edgeCost(curPos, nbPos, curMs);
        if (!ec) continue;
        const newCost = cost[cur] + ec.timeH + ec.penalty;
        if (newCost < cost[nb]) {
          cost[nb] = newCost;
          timeH[nb] = timeH[cur] + ec.timeH;
          came[nb] = cur;
          open.push(newCost + heur(nb), nb);
        }
      }
    }
  }

  if (came[destIdx] === -1 && startIdx !== destIdx) return null;

  // Reconstruct the grid path, then bookend with the exact start/destination.
  const gridPath: number[] = [];
  for (let i = destIdx; i !== -1; i = came[i]) {
    gridPath.push(i);
    if (i === startIdx) break;
  }
  gridPath.reverse();

  const positions: [number, number][] = [start, ...gridPath.map(posOf), destination];
  return {
    waypoints: simplify(positions),
    totalTimeH: timeH[destIdx],
    expanded,
  };
}

/**
 * Drop points that sit on a straight run (same bearing within a small threshold),
 * keeping the turns. Conservative: it never cuts a corner, so it can't shortcut
 * across something the grid path went around.
 */
export function simplify(points: [number, number][], bearingTolDeg = 8): [number, number][] {
  if (points.length <= 2) return dedupe(points);
  const pts = dedupe(points);
  if (pts.length <= 2) return pts;
  const out: [number, number][] = [pts[0]];
  for (let i = 1; i < pts.length - 1; i++) {
    const inB = rhumbBearingDeg(pts[i - 1], pts[i]);
    const outB = rhumbBearingDeg(pts[i], pts[i + 1]);
    let d = Math.abs(((inB - outB + 540) % 360) - 180);
    if (d > bearingTolDeg) out.push(pts[i]);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/**
 * Line-of-sight ("string pulling") smoothing over an ordered path. This is the
 * pass that kills the little dog-legs an 8-direction grid search leaves behind:
 * a run whose true bearing isn't a neat multiple of 45 degrees comes out of A*
 * as a staircase, and plain collinear simplify keeps every step as a turn.
 *
 * We walk the path keeping an ANCHOR (the last point we've committed to). For
 * each point we ask whether the straight run from the anchor to the point BEYOND
 * it stays clear (isClear returns true). While it does, the point in between is a
 * staircase artefact and gets dropped. The moment the anchor can no longer see
 * past a point, that point is a real corner (the path only bends there to dodge
 * land or shallow), so we keep it and make it the new anchor.
 *
 * Conservative by construction: every segment we emit has been checked with
 * isClear, so smoothing can never straighten a jog across water the router
 * considers unsafe. Wire isClear to the router's OWN navigability test so the two
 * can't disagree about what's passable.
 */
export function stringPull(
  points: [number, number][],
  isClear: (a: [number, number], b: [number, number]) => boolean,
): [number, number][] {
  const pts = dedupe(points);
  if (pts.length <= 2) return pts;
  const out: [number, number][] = [pts[0]];
  let anchor = 0;
  for (let i = 2; i < pts.length; i++) {
    if (!isClear(pts[anchor], pts[i])) {
      // The anchor can't see this point in a straight line, so the point before
      // it is a genuine corner. Keep it and re-anchor there.
      out.push(pts[i - 1]);
      anchor = i - 1;
    }
  }
  out.push(pts[pts.length - 1]);
  return out;
}

function dedupe(points: [number, number][]): [number, number][] {
  const out: [number, number][] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (!last || rhumbDistanceNm(last, p) > 1e-4) out.push(p);
  }
  return out;
}
